import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {clientConnection} from "../../Remoting/ClientConnection.js";
import {hostConnection} from "../../Remoting/HostConnection.js";
import {PhaseChange_ExtraMessage} from "./AlarmsComp.js";
import {SessionCoreData} from "../../../Server/DB/sessions/@Session.js";
import {LogOptions_ForGroupX} from "../../../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {LogType} from "../../../UI/Tools/@Shared/LogEntry.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

/** If a component's method is tagged with this, it will be called even when the component is disabled. */
export function AlwaysCall(target: object, key: string) {
	target[key].callEvenWhenCompDisabled = true;
}

/** This is returned by some base-class methods, as a typescript trick to ensure derived-classes call those base methods. */
const EngineSessionComp_ProofBaseMethodCalled = Symbol("EngineSessionComp_ProofBaseMethodCalled");

export type StatusUIObj = {
	ui: n|string|React.JSX.Element,
	omitWrapper?: boolean
};
export class EngineSessionComp<ConfigType = {}> {
	constructor(
		session: LiveSession, config: ConfigType, triggersEnabled_requirement: (session: LiveSession)=>boolean,
		/** For behaviorEnabled to end up true, this must return true, *and* triggersEnabled must be true. */
		behaviorEnabled_additionalRequirement: (session: LiveSession)=>boolean
	) {
		this.s = session;
		this.c = config;
		this.triggersEnabled = triggersEnabled_requirement(session);
		this.behaviorEnabled = this.triggersEnabled && behaviorEnabled_additionalRequirement(session);
	}
	s: LiveSession;
	c: ConfigType;
	/** Whether comp's triggers get activated. (only actually applied because the comp's GetTriggerPackages() function checks this field) */
	triggersEnabled = true; // todo: maybe rename to "baseEnabled"
	/** Whether comp receives normal broadcasts. (also toggles some other behaviors, eg. snoozing) */
	behaviorEnabled = true;

	// we'll need a more thoughtful approach to this whole system/block, once expanded to more comps
	IsSuspended() {
		if (this.c["enabled"] == false) return false; // if in-config disabled, don't consider suspended

		return this.behaviorEnabled && !this.triggersEnabled;
	}
	Unsuspend() {
		if (this.c["enabled"] == false) return EngineSessionComp_ProofBaseMethodCalled; // if in-config disabled, suspend/unsuspend do nothing

		//throw new Error("Method not implemented.");
		this.triggersEnabled = true;
		this.MaybeUpdateActiveState({compJustUnsuspended: true});
		return EngineSessionComp_ProofBaseMethodCalled;
	}
	Suspend() {
		if (this.c["enabled"] == false) return EngineSessionComp_ProofBaseMethodCalled; // if in-config-disabled, suspend/unsuspend do nothing

		//throw new Error("Method not implemented.");
		this.triggersEnabled = false;
		const resetActivatorStates = ()=>{
			for (const pack of this.triggerPackages) {
				if (pack.activator == null) continue;
				pack.activator.ResetStateForSuspend();
			}
		}
		resetActivatorStates();
		// kinda inelegant, but reset-state again in one tick; needed since, even though this comp's trigger-packages were just disabled,
		// ...their activators may still receive one final input-event which can mutate state
		// (namely, the input-event that is currently being processed / triggered this comp.Suspend() call -- if indeed that is the source)
		setTimeout(resetActivatorStates, 0);
		this.MaybeUpdateActiveState({compJustSuspended: true});
		return EngineSessionComp_ProofBaseMethodCalled;
	}

	readonly active = false;
	MaybeUpdateActiveState(opt: {sessionStarting?: boolean, sessionStopping?: boolean, compJustSuspended?: boolean, compJustUnsuspended?: boolean} = {}) {
		const oldActive = this.active;
		const newActive = this.behaviorEnabled && !this.IsSuspended() && !opt.sessionStopping;
		if (newActive != oldActive && newActive) {
			this["active" as any] = true; // these are the only two lines allowed to modify the "active" field
			this.OnActivate(!!opt.sessionStarting);
		} else if (newActive != oldActive && !newActive) {
			this["active" as any] = false; // these are the only two lines allowed to modify the "active" field
			this.OnDeactivate(!!opt.sessionStopping);
		}
	}

	async TryCallOnHostComp(methodName: string, ...args: any[]) {
		const compTypeName = this.constructor.name;
		if (!this.s.IsClient()) return; // we're already on local/host, so do nothing
		//if (this.s.IsLocal()) return this.s.remoteBridge.CallInternal("TryCallOnComp", compTypeName, methodName, ...args); // we're already on local/host, so call within this instance*/

		return await hostConnection!.bridge.Call("TryCallOnComp", compTypeName, methodName, ...args);
	}
	async TryCallOnClientComp(methodName: string, ...args: any[]) {
		const compTypeName = this.constructor.name;
		if (!this.s.IsLocal()) return; // we're already on client, so do nothing
		//if (this.s.IsRemote()) return this.s.remoteBridge.CallInternal("TryCallOnComp", compTypeName, methodName, ...args); // we're already on remote, so call within this instance*/

		return await clientConnection!.bridge.Call("TryCallOnComp", compTypeName, methodName, ...args);
	}

	/*get c() { return this.s.c.snoozeAndPrompts.memoryPrompt; }
	get Enabled() { return this.s.c.snoozeAndPrompts.enabled && this.c.enabled; }*/

	Log(message: string, type = LogType.Event_Small, opt?: LogOptions_ForGroupX) {
		this.s.Log(`[${this.constructor.name}] ${message}`, type, opt);
	}

	// methods that are supposed to be overriden, in many component classes
	// (rather than optional methods, just have an empty implementation for each -- makes calling code simpler)
	// ==========


	triggerPackages = [] as TriggerPackage[]; // populated by FBASession.AddTriggerPackage
	GetTriggerPackages(): TriggerPackage[] { return []; }
	GetStatusUI?(): n|string|React.JSX.Element|StatusUIObj;

	/** Called prior to general init. Used for, eg. code that must run right after a user action (start-session click). */
	OnStart_Early() {}
	/** Note: This gets called even if ModeSwitcherComp made the component start out suspended. */
	OnStart(recoveryInfo: SessionCoreData|n) {}
	OnActivate(sessionStarting: boolean) {}
	OnStop(saveLocalData: boolean) {}
	OnDeactivate(sessionStopping: boolean) {}
	OnInitialDelayCompleted() {}

	OnStartPhase(oldPhase: Phase, newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnLeavePhase(newPhase: Phase, oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}

	OnStartPhase_Tech(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnLeavePhase_Tech(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnStartPhase_Drifting(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnLeavePhase_Drifting(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnStartPhase_Dreaming(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnLeavePhase_Dreaming(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnStartPhase_Solving(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnLeavePhase_Solving(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnStartPhase_Delving(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}
	OnLeavePhase_Delving(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {}

	//PreSnooze(options: SnoozeOptions) {}
}